<template>
    <LayoutBlock
        :tutorial_dialog="tutorial_dialog"
        ref="layoutBlock"
        v-on:closeDialog="handleCloseDialog"
        @weightsConfirmed="weightsConfirmed"
        >
        <div class="d-flex flex-column" v-if="measures && updated">
            <div class="pl-2 pb-2 subtitle-1" v-if="conf.title" >{{conf.title}}</div>
            <v-container class="ma-0 pa-0">
            <v-row no-gutters>
                <v-col cols="6">
                    <NumericField dense outlined
                        :readonly="preview"
                        label="Valore nominale"
                        v-model="measures.nom"
                        :suffix="data.instrument_data.uom"
                    ></NumericField>
                </v-col>
                <v-col cols="6" class="pl-2">
                    <v-btn @click="openWeightSelection(measures.nom, measures.weights)" v-if="!preview">Scegli pesi</v-btn>
                    <v-btn class="ml-4" @click="openWeightSelected([{nom: measures.nom, weights: measures.weights}])" v-if="!preview">Vedi pesi</v-btn>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col cols="12">
                    <v-img width="200" height="200" :src="image_data" />
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col cols="12" class="d-flex justify-end py-2" v-if="!preview">
                    <v-btn color="primary" @click="autoFill(measures.items)">Riempimento automatico</v-btn>
                </v-col>
            </v-row>
            <v-row no-gutters>
            <v-col cols="12">
                <v-simple-table class="measure_table">
                <template v-slot:default>
                <thead class="expansion">
                    <tr>
                        <th class="table-text-blocks text-center">{{ data.block.conf.headers.count }}</th>
                        <th class="table-text-blocks text-right">{{ data.block.conf.headers.nom }} <br/><i class="caption">({{data.instrument_data.uom}})</i></th>
                        <th class="table-text-blocks text-right">{{ data.block.conf.headers.zero }} <br/><i class="caption">({{data.instrument_data.uom}})</i></th>
                        <th class="table-text-blocks text-right">{{ data.block.conf.headers.reading }} <br/><i class="caption">({{data.instrument_data.uom}})</i></th>
                        <th class="table-text-blocks text-right">{{ data.block.conf.headers.difference }} <br/><i class="caption">({{data.instrument_data.uom}})</i></th>
                        <th class="table-text-blocks text-right" v-if="!is_OIML && measures.useTolerance">{{ data.block.conf.headers.toleranceError }} <br/><i class="caption">({{computeToleranceType.uom}})</i></th>
                    </tr>
                </thead>
                <tr v-for="(item,index) of measures.items" :key="index">
                    <td class="table-blocks d-flex justify-center">
                        <v-icon class="d-flex flex-shrink-1 flex-grow-0" v-if="index >= conf.min_readings && !preview" @click="removeRow(index)">delete</v-icon>
                        <div v-else class="d-flex pr-4"></div>
                        <div class="d-flex justify-center flex-shrink-0 flex-grow-1 pr-1">{{index + 1}}</div>
                    </td>
                    <td class="table-blocks">
                        <NumericField dense outlined 
                            hide-details 
                            disabled 
                            placeholder="0" 
                            v-model="item.nom" 
                        ></NumericField>
                    </td>
                    <td class="table-blocks">
                        <NumericField dense outlined 
                            hide-details 
                            disabled 
                            placeholder="0" 
                            v-model="item.zero" 
                            :precision="currentPrecision"></NumericField>
                    </td>
                    <td class="table-blocks">
                        <NumericField dense outlined 
                            solo hide-details 
                            @input="calculateRowWrapper(item, index)" 
                            placeholder="0" 
                            :readonly="preview" 
                            v-model="item.reading" 
                            :class="readingWarning(item.reading)" 
                            :precision="currentPrecision"></NumericField>
                    </td>
                    <td class="table-blocks">
                        <NumericField dense outlined 
                            hide-details 
                            disabled 
                            placeholder="0" 
                            v-model="item.difference"
                            :negative="true"
                            ></NumericField>
                    </td>
                    <td class="table-blocks" v-if="!is_OIML && measures.useTolerance">
                        <NumericField dense outlined 
                            hide-details 
                            disabled 
                            placeholder="0" 
                            v-model="item.toleranceError"
                            :negative="true"
                            ></NumericField>
                    </td>
                    
                </tr>
                </template>
                </v-simple-table>
            </v-col>
            <v-col sm="6" offset-sm="6" class="d-flex flex-grow-1 flex-shrink-0 justify-end">
                <v-divider class="pl-4" vertical></v-divider>
                <div class="d-flex flex-column align-self-start my-2 pl-4">
                    <v-checkbox label="Verifica conformità" :disabled="preview" v-model="measures.useTolerance"></v-checkbox>
                    <NumericField dense outlined
                        v-model="measures.max_error"
                        disabled
                        label="errore massimo"
                        :suffix="computeToleranceType.uom"
                    />
                    <span v-if="measures.useTolerance" class="my-2">
                        tolleranza: <i>{{computeToleranceType.type}}, {{computeToleranceType.value}}</i>
                    </span>
                    <NumericField dense outlined
                        v-if="measures.useTolerance"
                        v-model="measures.tolerance"
                        label="valore"
                        disabled
                        :prepend-icon="addWarning"
                        :suffix="computeToleranceType.uom"
                    />
                    <span v-if="measures.useTolerance">
                    <StatusIcon 
                    :status="measures.result" 
                    :tooltip_text="block_tooltip[measures.result]" />
                    {{ computedStatus }}
                    </span>
                </div>
            </v-col>
            </v-row>
            <v-row no-gutters>
                <div class="d-flex flex-column flex-grow-1 flex-shrink-0 table-bordered ma-2">
                    <div v-if="precision_overflow" class="d-flex error--text caption"><StatusIcon status="failed" small /><b>Controlla valori e precisione letture</b></div>
                </div>        
                <div class="d-flex flex-grow-0 flex-shrink-1 my-2">
                    <v-btn v-if="checkStatic && !preview" color="primary" @click="addRow"><v-icon class="pa-2">add_box</v-icon>aggiungi misura</v-btn>
                </div>
            </v-row>
            </v-container>
        </div>
    </LayoutBlock>
</template>
<script>
const nerdamer = require('nerdamer/all')

import { eccentricity_item_scie } from '../../../utils/itemBlockSchema'
import {BaseIso} from "../../../utils/baseIso"
import {convertPrimaryWeights} from "../../../utils/commonUtils"
// import _ from 'lodash'
import LayoutBlock from "../../../components/utils/LayoutBlock"
import StatusIcon from "../../../components/utils/StatusIcon"
import Big from "big.js"

export default {
    components: {LayoutBlock, StatusIcon},
    mixins: [BaseIso],
    computed: {
        rowSpan(){
            return this.measures.items.length
        },
        
    },
    methods: {
        calculateError(){
            let block_result = this.measures.items.reduce(function(result, item) {
                let res = 'done'
                switch (item.result) {
                    case 'uncomplete':
                        res = 'uncomplete'
                        break
                    case 'failed':
                        if (result == 'uncomplete') {
                            res = 'uncomplete'
                        } else {
                            res = 'failed'
                        }
                        break
                    case 'done':
                        res = result
                        break
                }
                return res
            }, "done")
            if(block_result != "uncomplete"){
                if(this.formulas && this.formulas['formula:massimo']){
                    let formula_max = this.formulas['formula:massimo']
                    let formatted_list = 0
                    let diff_list = this.measures.items.map(x => {
                            if(x.difference){
                                return this.round(Big(x.difference).abs())
                            }
                        })
                    if(!this.is_OIML){
                        formatted_list = this.measures.items.map(x => {
                            if(x.toleranceError){
                                return this.round(x.toleranceError)
                            }
                            else {
                                return this.round(0)
                            }
                        })
                    }
                    let paramName = Object.keys(formula_max.data)[0] // name of the variable, like a in stdev(a)
                    let temp = this.is_OIML ? diff_list : formatted_list
                    var test = formula_max.equation.replace(paramName, temp.toString())
                    this.measures.max_error = nerdamer(test).evaluate().text()
                    block_result = !this.precision_overflow && this.measures.tolerance && Big(this.measures.max_error).lte(this.measures.tolerance) ? 'done' : 'failed'
                    var test2 = formula_max.equation.replace(paramName, diff_list.toString())
                    this.measures.max_diff = this.round(nerdamer(test2).evaluate().text())
                } else {
                    if(!this.measures.max_error){
                        this.measures.max_error = this.round(0)
                    }
                    if(!this.measures.max_diff){
                        this.measures.max_diff = this.round(0)
                    }
                }
            }
            this.measures.result = block_result
        },
        calculateRowWrapper(item, index){
            if(index == 0){
                if (item.reading == null || item.reading == '') {
                    item.result = 'uncomplete'
                } else {
                    item.result = 'done'
                }
                for(let i = 1; i< this.measures.items.length; i++){
                    this.calculateRow(this.measures.items[i])
                }
            } else {
                this.calculateRow(item)
            }
        },
        calcBlockResult(){
        },
        calculateRow(item){
            let firstPoint = this.measures.items[0]
            if (item.reading == null || item.reading == '') {
                item.result = 'uncomplete'
            } else {
                item.result = 'done'
            }
            if(item.reading && item.nom && firstPoint.reading){
                let range = this.calcMinRange(item.reading)
                let precision = this.currentPrecision
                if(range){
                    precision = this.countDecimals(range.d)
                }
                item.difference = this.round(Big(item.reading).minus(firstPoint.reading), precision)
                if(!this.is_OIML){
                    item.toleranceError = this.handleToleranceError(item)
                }
            }
            this.calculateError()
        },
        addRow(){
            let item = eccentricity_item_scie()
            this.measures.items.push(item)
        },
        calculateAll(weights){
            let weightsum = weights.reduce((a,b) => Big(a).plus(b.conv).toString(),0)
            this.measures.nom = weights.reduce((a,b) => Big(a).plus(b.name).toString(),0)
            if(this.is_OIML){
                this.measures.tolerance = this.calc_OIML(this.calcMinRange(weightsum), weightsum)
            }
            for(let elem of this.measures.items){
                elem.nom = weightsum
                this.calculateRow(elem)
            }

        },
        async buildMeasures(){
            this.measures = {
                weights: [],
                items: [],
                result: 'uncomplete',
                max_error: "0",
                max_diff: "0",
                nom: null,
                useTolerance: false,
                tolerance: null,
                toleranceData: null,
                image_id: null
            }
            this.buildTolerances()
            await this.getImageID()
            await this.getImage()
            for(let i = 0; i< this.data.block.conf.min_readings; i++){
                let item = eccentricity_item_scie()
                item.difference = this.round(item.difference)
                this.measures.items.push(item)
            }
            this.measures.nom = await this.buildWithCategory()
            if(this.measures.nom){
                let autoSelected = this.autoSelection(this.measures.nom)
                if(autoSelected.length > 0 ){
                    this.weightsConfirmed(autoSelected)
                }
            }
        },
        weightsConfirmed(weights){
            this.measures.weights = _.cloneDeep(weights)
            let weightItems = convertPrimaryWeights(weights, _.cloneDeep(this.data.primaries), this.instrument_data.uom)
            // console.log("$$ weightItems", weightItems)
            this.calculateAll(weightItems)
        },
        handleInvalidPrimaries(){
            if(this.data.primaries && this.data.invalidPrimaries){
                // console.log("$$ resetting eccentricity")
                this.measures.items.forEach(item => {
                    item.nom = 0
                })
                this.measures.weights.splice(0,this.measures.weights.length)
                this.measures.tolerance = null
            }
        },
        handleToleranceChange(){
            for(let item of this.measures.items){
                item.toleranceError = this.handleToleranceError(item)
            }
        }
    }
}
</script>